/**
 * Regole pure del sentiment in pagina (F-177, F-178): l'età di una lettura e
 * le sintesi dello storico. In un modulo a parte come `briefing-history.ts`,
 * così si provano senza montare niente.
 */

import type { SentimentDirection, SentimentHistoryPoint } from './sentiment-api';

export const DIRECTION_LABEL: Record<SentimentDirection, string> = {
  risk_on: 'propensione al rischio',
  risk_off: 'avversione al rischio',
  neutral: 'piatto',
  mixed: 'segnali contrastanti',
};

export const DIRECTION_TONE: Record<SentimentDirection, 'positive' | 'negative' | 'neutral' | 'warning'> = {
  risk_on: 'positive',
  risk_off: 'negative',
  neutral: 'neutral',
  mixed: 'warning',
};

export const DIVERGENCE_LABEL = { aligned: 'di pari passo', opposed: 'contrapposti', unclear: 'confronto non possibile' } as const;

/** Oltre questa età la lettura mostrata è «datata»: un giorno e mezzo, cioè saltata almeno una volta. */
export const STALE_AFTER_HOURS = 36;

/**
 * L'età di una lettura, a parole: «adesso», «25 min fa», «3 h fa», «ieri»,
 * «4 giorni fa». Un istante nel futuro (orologi disallineati) è «adesso»,
 * mai un'età negativa.
 */
export function formatAge(iso: string, now: Date = new Date()): string {
  const minutes = Math.floor((now.getTime() - Date.parse(iso)) / 60_000);
  if (!Number.isFinite(minutes) || minutes < 1) return 'adesso';
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h fa`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'ieri' : `${days} giorni fa`;
}

export function isStale(iso: string, now: Date = new Date()): boolean {
  return now.getTime() - Date.parse(iso) > STALE_AFTER_HOURS * 3_600_000;
}

/** Il valore di un indicatore in un punto dello storico, o `null` se quel giorno non era misurato. */
export function figureValue(point: SentimentHistoryPoint, name: string): string | null {
  return point.figures.find((figure) => figure.name === name)?.value ?? null;
}

export type DomainSummary = {
  readings: number;
  failed: number;
  /** Media degli score, arrotondata; `null` senza letture completate. */
  averageScore: number | null;
  riskOn: number;
  riskOff: number;
  /** Quante volte la direzione è cambiata da una lettura completata alla successiva. */
  changes: number;
};

/**
 * La sintesi di un ambito sulla finestra. ⚠️ I giorni falliti non entrano
 * nella media né nei cambi: non hanno un esito, e contarli come zero
 * trascinerebbe la media verso il neutro.
 */
export function summarizeDomain(points: readonly SentimentHistoryPoint[]): DomainSummary {
  const completed = points.filter((point) => point.status === 'completed' && point.direction !== null);
  const scores = completed.map((point) => point.score).filter((score): score is number => score !== null);
  let changes = 0;
  for (let index = 1; index < completed.length; index += 1) {
    if (completed[index]!.direction !== completed[index - 1]!.direction) changes += 1;
  }
  return {
    readings: completed.length,
    failed: points.length - completed.length,
    averageScore: scores.length > 0 ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length) : null,
    riskOn: completed.filter((point) => point.direction === 'risk_on').length,
    riskOff: completed.filter((point) => point.direction === 'risk_off').length,
    changes,
  };
}

export function signedScore(score: number | null): string {
  if (score === null) return '—';
  return score > 0 ? `+${score}` : String(score);
}
